import {i18n} from '@online-offer/i18n';
import {Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {Fragment, useContext} from 'react';

import {isEmpty, isNil} from 'ramda';

import {Nullish} from 'shared/web';

import {FinancialProductCalculation, FinancialProductOffer} from '../../api/types/onlineOfferTypes';
import {CardOo} from '../../components/CardOo/CardOo';
import {SectionId} from '../../consts/Sections';
import {useFinancialProducts} from '../../hooks/useFinancialProducts';
import {requestOpenFinancialProduct} from '../FinancialProducts/financialProductNavigation';
import {
  FinancialProductPreferenceContext,
  type FinancialProductPreferenceContextValue,
} from '../FinancialProducts/FinancialProductPreferenceContext';
import {formatFinancialProductMoney} from '../FinancialProducts/formatFinancialProductMoney';
import {FinancialProductSummaryRow} from './FinancialProductSummaryRow';

const FINANCIAL_PRODUCTS_SECTION_ID: SectionId = 'FINANCIAL_PRODUCTS';

/** A leasing with monthly services is quoted at its total, as the section headlines it. */
const toQuotedMonthlyPayment = (calculation: FinancialProductCalculation | Nullish) =>
  calculation?.leasingTotalMonthlyPayment ?? calculation?.monthlyPayment;

const toStoredMonthlyPayment = (offer: FinancialProductOffer | Nullish) =>
  offer?.availability === 'AVAILABLE' ? toQuotedMonthlyPayment(offer.calculation) : undefined;

/**
 * With the customer's choice at hand the card quotes what the section shows, local quotes
 * included. Without a shown quote (withheld rates, an older backend) it keeps the stored one.
 */
const toMonthlyPayment = (
  preference: FinancialProductPreferenceContextValue | null,
  offer: FinancialProductOffer | Nullish
) => {
  if (isNil(preference) || isNil(offer)) {
    return toStoredMonthlyPayment(offer);
  }

  return match(preference.getDisplayed(offer.productType))
    .with({source: 'LOCAL'}, (local) => toQuotedMonthlyPayment(local.quote.calculation))
    .with({source: 'STORED'}, {source: 'NONE'}, () => toStoredMonthlyPayment(offer))
    .exhaustive();
};

/** Standard and balloon share the cheapest financing card; leasing remains separate. */
export function FinancialProductSummary() {
  const preference = useContext(FinancialProductPreferenceContext);
  const cache = useFinancialProducts();
  const formatCurrency = useFormatCurrency();
  // The choice's authoritative body also covers acknowledgements and refusals not yet polled.
  const financialProducts = isNil(preference) ? cache.financialProducts : preference.products;

  const monthlyPaymentOf = (offer: FinancialProductOffer | Nullish) =>
    toMonthlyPayment(preference, offer);

  const financing = [financialProducts?.standard, financialProducts?.balloon]
    .sort(
      (left, right) =>
        Number(monthlyPaymentOf(left)?.amount ?? Infinity) -
        Number(monthlyPaymentOf(right)?.amount ?? Infinity)
    )
    .at(0);

  const quotes = [
    {
      id: 'financing',
      label: i18n.t('financialProducts.summary.financingFrom'),
      monthlyPayment: monthlyPaymentOf(financing),
      productType: financing?.productType,
    },
    {
      id: 'leasing',
      label: i18n.t('financialProducts.summary.leasingFrom'),
      monthlyPayment: monthlyPaymentOf(financialProducts?.leasing),
      productType: 'LEASING' as const,
    },
  ].flatMap((quote) =>
    isNil(quote.monthlyPayment) || isNil(quote.productType)
      ? []
      : [{...quote, monthlyPayment: quote.monthlyPayment, productType: quote.productType}]
  );

  if (isEmpty(quotes)) {
    return null;
  }

  return (
    <CardOo backgroundColor="semantic.bg.subtle">
      <VStack spacing={5}>
        {quotes.map((quote, index) => (
          <Fragment key={quote.id}>
            <Show when={index > 0}>
              <Separator spacing={0} />
            </Show>
            <FinancialProductSummaryRow
              id={quote.id}
              label={quote.label}
              amount={formatFinancialProductMoney(quote.monthlyPayment, formatCurrency)}
              onCalculate={() => {
                requestOpenFinancialProduct(quote.productType);
                document
                  .getElementById(FINANCIAL_PRODUCTS_SECTION_ID)
                  ?.scrollIntoView({behavior: 'smooth', block: 'start'});
              }}
            />
          </Fragment>
        ))}
      </VStack>
    </CardOo>
  );
}
